"use client";

import { MouseEvent, SyntheticEvent, useEffect, useRef } from "react";

import { ArrowBackWhiteIcon } from "@/components/icons/ArrowBackWhiteIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useLearning } from "@/context/NutrafolAcademyContext";
import { GA4 } from "@/utils/ga4Utils";
import { getVideoProgress } from "@/utils/videoTracking";

import CourseInfoPanel from "./CourseInfoPanel";

const CourseVideo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSaveRef = useRef(0);

  const {
    courses,
    currentStep,
    quizSelections,
    videoProgress,
    setVideoTime,
    handleVideoEnded,
    switchProduct,
  } = useLearning();

  const course = courses[currentStep];
  const optionData = quizSelections[currentStep] || null;
  const videoData = optionData?.video ? optionData.video : course?.video;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const saved = Number(videoProgress[currentStep] || 0);
    const onLoaded = () => {
      if (saved > 0 && saved < (video.duration || Infinity)) {
        video.currentTime = saved;
      }
    };

    video.addEventListener("loadedmetadata", onLoaded);
    return () => video.removeEventListener("loadedmetadata", onLoaded);
  }, [currentStep, videoProgress, videoData?.src]);

  const handleTimeUpdate = (e: SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const now = Date.now();
    if (now - lastSaveRef.current > 1000) {
      setVideoTime(currentStep, video.currentTime);
      lastSaveRef.current = now;
    }
  };

  const switchProductHandler = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    videoRef.current?.pause();
    switchProduct();
  };

  if (!course) return null;

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-12 max-w-[400px] md:max-w-none mx-auto w-full h-full">
      <div className="w-full lg:w-[59%]">
        <CourseInfoPanel course={course} tagLabel={`Course ${currentStep + 1}`}>
          {optionData?.productName ? (
            <div className="pt-2 mt-2 border-t border-nova-transparency-white-20">
              <div className="flex justify-between items-center py-1 gap-2 flex-wrap">
                <TextElementNova
                  text={optionData.productName}
                  className="Body-10-Dia-Normal text-nova-base-white"
                />
                <button
                  type="button"
                  className="flex items-center gap-1 border-b border-nova-base-white text-nova-base-white"
                  aria-label="Learn About a Different Formula"
                  onClick={switchProductHandler}
                >
                  <ArrowBackWhiteIcon />
                  <TextElementNova
                    text="Learn About a Different Formula"
                    className="CTA-10-Dia-Normal text-nova-base-white !leading-[140%]"
                  />
                </button>
              </div>
            </div>
          ) : null}
        </CourseInfoPanel>
      </div>

      <div className="w-full h-full lg:w-[41%] overflow-hidden">
        <video
          className="w-full h-full object-contain md:object-cover bg-[black]"
          key={videoData?.src}
          ref={videoRef}
          poster={videoData?.poster}
          loop={false}
          controls
          autoPlay
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleVideoEnded}
          onPlay={(e) => {
            GA4.onVideoPlayed({
              module_state: "Play",
              video_name: `Course ${currentStep + 1}`,
              video_url: videoData?.src ?? "",
              video_play_type: "autoplay",
              ...getVideoProgress(e.currentTarget),
            });
          }}
          onPause={(e) => {
            if (videoRef.current?.ended) return;
            GA4.onVideoPlayed({
              module_state: "Paused",
              video_name: `Course ${currentStep + 1}`,
              video_url: videoData?.src ?? "",
              video_play_type: "autoplay",
              ...getVideoProgress(e.currentTarget),
            });
          }}
        >
          {videoData?.src ? <source src={videoData.src} type="video/mp4" /> : null}
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
};

export default CourseVideo;
